import { ErrorHandler, Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'

import { TuettuKieli } from '@lemon/core/model/common'
import { AreYouSureDialog, AreYouSureDialogData } from '../../_jaettu-angular/_components/are-you-sure.dialog'
import { LaskuttavaAsiakasProvider } from '../../_jaettu-angular/service/lasku/laskuttava-asiakas.service'

import {
  Asiakas,
  CustomerPrices,
  Kayttaja,
  KayttajanTiedot,
  LemonaidAsiakkaanKuukausihinta,
  LemonaidAsiakkaanMuuJatkuvaVeloitus,
  myyjanaAlvVelvollisetTilat,
  PaivitaAsiointikieliRequest,
  PaivitaAsiointikieliResponse,
} from '@lemon/core-firebase/model/kayttaja'

import { setExtra, setUser } from '@sentry/browser'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { Observable, firstValueFrom, of } from 'rxjs'
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators'
import { FirebaseLemonaid } from './firebase-lemonaid.service'

@Injectable()
export class LaskuttavaAsiakasLemonaidProvider extends LaskuttavaAsiakasProvider {
  constructor(private kayttajaService: KayttajaService) {
    super()
  }

  public annaLaskuttavaAsiakasAvain(): Promise<string | null> {
    return this.kayttajaService.getKayttaja().then(kayttaja => {
      return kayttaja.asiakasAvain
    })
  }

  public onkoOssRekisterissa(): Observable<boolean> {
    return this.kayttajaService.nykyinenAsiakasObservable.pipe(map(asiakas => !!asiakas?.ossRekisterissa))
  }

  public onkoAlvRekisterissa(): Observable<boolean> {
    return this.kayttajaService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        const rekisteritTuntemattomat = !!asiakas && !asiakas.rekisterit
        if (rekisteritTuntemattomat) {
          return true
        }
        const alvRekisterinTila = asiakas?.rekisterit?.alv?.tyyppi
        return !!alvRekisterinTila && myyjanaAlvVelvollisetTilat.includes(alvRekisterinTila)
      }),
      distinctUntilChanged(),
    )
  }
}

@Injectable()
export class KayttajaService {
  kayttajaObservable: Observable<Kayttaja>
  kayttajanTiedotObservable: Observable<KayttajanTiedot>
  kayttajaaImpersonoidaanObservable: Observable<boolean>
  nykyinenAsiakasObservable: Observable<Asiakas>
  nykyisenAsiakkaanHinnatObservable: Observable<LemonaidAsiakkaanKuukausihinta[]>
  muutJatkuvatVeloituksetObservable: Observable<LemonaidAsiakkaanMuuJatkuvaVeloitus[]>

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private errorHandler: ErrorHandler,
    private _dialog: MatDialog,
  ) {
    this.kayttajanTiedotObservable = this._firebaseLemonaid.authUserObservable.pipe(
      switchMap(lemonaidUser => {
        if (lemonaidUser) {
          const lemonaidTokenPromise = lemonaidUser.getIdTokenResult()
          return lemonaidTokenPromise
            .then(result => {
              if (result && result.claims && result.claims.clientid && result.claims.asiakasavain) {
                const tiedot: KayttajanTiedot = { uid: lemonaidUser.uid, asiakasAvain: result.claims.asiakasavain as string, asiakasId: result.claims.clientid as string }
                if (result.claims.euid) {
                  tiedot.euid = result.claims.euid as string
                }
                this.asetaUserContext(tiedot, lemonaidUser.email)
                return tiedot
              } else {
                console.error('Claimeissa on jotakin vikana')
                return this._logoutImmediate()
                  .then(() => null)
                  .catch(er => this.errorHandler.handleError(er))
              }
            })
            .catch(err => {
              errorHandler.handleError(err)
              console.error('Error while logging in', err)
              return this._logoutImmediate()
                .then(() => null)
                .catch(er => this.errorHandler.handleError(er))
            })
        } else {
          // console.error('No user')
          return Promise.resolve(null)
        }
      }),
      lemonShare(),
    )

    this.kayttajaObservable = this.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (kayttajanTiedot) {
          return this._firebaseLemonaid
            .firestoreDoc<Kayttaja>('/kayttajat/' + kayttajanTiedot.uid)
            .listen()
            .pipe(
              switchMap(kayttaja => {
                if (kayttaja && !kayttaja.aktiivinen) {
                  return this.logout().then(() => null)
                }
                return of<Kayttaja>(kayttaja ?? null)
              }),
            )
        }
        return of<Kayttaja>(null)
      }),
    )

    this.kayttajaaImpersonoidaanObservable = this.kayttajanTiedotObservable.pipe(
      map(kayttajanTiedot => {
        if (kayttajanTiedot) {
          return !!kayttajanTiedot.euid
        }
        // Turvallisuussyistä oletuksena true, koska impersonointi rajaa toiminnallisuuksia
        return true
      }),
    )

    this.nykyinenAsiakasObservable = this.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (kayttajanTiedot) {
          return this._firebaseLemonaid.firestoreDoc<Asiakas>('/customers/' + kayttajanTiedot.asiakasAvain).listen()
        }
        return of<Asiakas>(null)
      }),
    )

    this.nykyisenAsiakkaanHinnatObservable = this.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (kayttajanTiedot) {
          return this._firebaseLemonaid
            .firestoreDoc<CustomerPrices>('customers/' + kayttajanTiedot.asiakasAvain + '/customer-prices/' + kayttajanTiedot.asiakasAvain)
            .listen()
            .pipe(map(customerPrices => customerPrices?.hinnat))
        }
        return of<LemonaidAsiakkaanKuukausihinta[]>([])
      }),
    )

    this.muutJatkuvatVeloituksetObservable = this.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (kayttajanTiedot) {
          return this._firebaseLemonaid
            .firestoreDoc<CustomerPrices>('customers/' + kayttajanTiedot.asiakasAvain + '/customer-prices/' + kayttajanTiedot.asiakasAvain)
            .listen()
            .pipe(map(customerPrices => customerPrices?.muutJatkuvatVeloitukset || []))
        }
        return of<LemonaidAsiakkaanMuuJatkuvaVeloitus[]>([])
      }),
    )
  }

  private asetaUserContext(kayttajanTiedot: KayttajanTiedot, email: string) {
    if (kayttajanTiedot) {
      setUser({
        id: kayttajanTiedot.uid, // Your internal identifier for the user.
        username: email, // The username. Generally used as a better label than the internal ID.
        email: email, // An alternative to a username (or addition). Sentry is aware of email addresses and can show things like Gravatars, unlock messaging capabilities, and more.
        // ip_address
      })
      setExtra('asiakasId', kayttajanTiedot.asiakasId)
      setExtra('asiakasAvain', kayttajanTiedot.asiakasAvain)
    } else {
      setUser(null)
      setExtra('asiakasId', null)
      setExtra('asiakasAvain', null)
    }
  }

  private _logoutImmediate(): Promise<any> {
    return this._firebaseLemonaid.authSignOut()
  }

  public logout(): Promise<any> {
    return this.timeoutPromise(50)
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this.timeoutPromise(50)
      })
      .then(() => {
        return this._logoutImmediate()
      })
  }

  private timeoutPromise(millis: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const id = setTimeout(() => {
        clearTimeout(id)
        resolve()
      }, millis)
    })
  }

  getKayttajanTiedot(): Promise<KayttajanTiedot> {
    return firstValueFrom(this.kayttajanTiedotObservable)
  }

  getKayttaja(): Promise<Kayttaja> {
    return firstValueFrom(this.kayttajaObservable)
  }

  getKayttajaaImpersonoidaan(): Promise<boolean> {
    return firstValueFrom(this.kayttajaaImpersonoidaanObservable)
  }

  async tallennaKayttajanKieli(kieli: TuettuKieli): Promise<void> {
    const kayttaja = await firstValueFrom(this.kayttajaObservable)
    if (!kayttaja) {
      throw new Error('Ei käyttäjää!')
    }

    const requestData: PaivitaAsiointikieliRequest = {
      kieli: kieli,
    }
    const resp = await this._firebaseLemonaid.functionsCall<PaivitaAsiointikieliRequest, PaivitaAsiointikieliResponse>('kayttajaPaivitaAsiointikieli', requestData)

    if (!resp || resp.e) {
      throw new Error('Asiointikielen tallentaminen epäonnistui! ' + (resp?.e || 'No response'))
    }

    /** Success, do nothing */
  }

  async kayttajaaImpersonoidaan(): Promise<boolean> {
    const impersonoidaan = await firstValueFrom(this.kayttajaaImpersonoidaanObservable)
    if (impersonoidaan) {
      const data: AreYouSureDialogData = {
        header: 'Toiminto estetty',
        text: 'Tätä toimintoa ei voi käyttää impersonoidessa toista käyttäjää',
        rightAction: 'OK',
      }
      this._dialog.open(AreYouSureDialog, { data, width: '400px' })
      return true
    }
    return false
  }
}
